import React, { useCallback, useState } from 'react';
import omit from 'lodash/omit';
import isNil from 'lodash/isNil';

import { ID, I18nText } from '../../../types';
import { ProjectNanoID } from '../../../main/projects/projectsTypes';

import { useMountEffect } from '../../../common/hooks/useMountEffect';

import { UniversalFilterSavedState } from './utils/UniversalFilterSavedState';

import {
  UniversalFilterTypes,
  UniversalFilterView
} from './UniversalFilter.types';

import {
  UniversalFilterCheckbox,
  UniversalFilterCheckboxOnChange
} from './components/UniversalFilterCheckbox';
import {
  UniversalFilterCompanies,
  UniversalFilterCompaniesOnChange
} from './components/UniversalFilterCompanies';
import {
  UniversalFilterDate,
  UniversalFilterDateOnChange
} from './components/UniversalFilterDate';
import {
  UniversalFilterOperationState,
  UniversalFilterOperationStateOnChange
} from './components/UniversalFilterOperationState';
import {
  UniversalFilterOperationType,
  UniversalFilterOperationTypeOnChange
} from './components/UniversalFilterOperationType';
import {
  UniversalFilterProjectStatus,
  UniversalFilterProjectStatusOnChange
} from './components/UniversalFilterProjectStatus';
import {
  UniversalFilterTags,
  UniversalFilterTagsOnChange
} from './components/UniversalFilterTags';
import {
  UniversalFilterInvoiceStatusFilterOptions,
  UniversalFilterTaskStatus,
  UniversalFilterTaskStatusFilterOptions,
  UniversalFilterTaskStatusOnChange
} from './components/UniversalFilterTaskStatus';
import {
  UniversalFilterText,
  UniversalFilterTextFilterOptions,
  UniversalFilterTextOnChange
} from './components/UniversalFilterText';
import {
  UniversalFilterTextPlain,
  UniversalFilterTextPlainOnChange
} from './components/UniversalFilterTextPlain';
import {
  UniversalFilterUsers,
  UniversalFilterUsersFilterOptions,
  UniversalFilterUsersOnChange
} from './components/UniversalFilterUsers';
import {
  UniversalFilterUsersExtended,
  UniversalFilterUsersExtendedFilterOptions,
  UniversalFilterUsersExtendedOnChange
} from './components/UniversalFilterUsersExtended';
import {
  UniversalFilterNumber,
  UniversalFilterNumberFilterOptions,
  UniversalFilterNumberOnChange
} from './components/UniversalFilterNumber';
import {
  UniversalFilterInvoices,
  UniversalFilterInvoicesFilterOptions,
  UniversalFilterInvoicesOnChange
} from './components/UniversalFilterInvoices';
import {
  UniversalFilterItemTags,
  UniversalFilterItemTagsFilterOptions,
  UniversalFilterItemTagsOnChange
} from './components/UniversalFilterItemTags';
import {
  UniversalFilterItemBindingStatusOnChange,
  UniversalFilterItemBindingStatus
} from './components/UniversalFilterItemBindingStatus';
import {
  UniversalFilterTaskItems,
  UniversalFilterTaskItemsOnChange
} from './components/UniversalFilterTaskItems';
import {
  UniversalFilterInvoiceProcessingJobStatus,
  UniversalFilterInvoiceProcessingJobStatusFilterOptions,
  UniversalFilterInvoiceProcessingJobStatusOnChange
} from './components/UniversalFilterInvoiceProcessingJobStatus';
import {
  UniversalFilterTagId,
  UniversalFilterTagIdOnChange
} from './components/UniversalFilterTagId';
import {
  UniversalFilterCompanyNanoId,
  UniversalFilterCompanyNanoIdOnChange
} from './components/UniversalFilterCompanyNanoId';
import {
  UniversalFilterCompanyStatus,
  UniversalFilterCompanyStatusOnChange
} from './components/UniversalFilterCompanyStatus';
import {
  UniversalFilterCompanyNewFinStatus,
  UniversalFilterCompanyNewFinStatusOnChange
} from './components/UniversalFilterCompanyNewFinStatus';
import {
  UniversalFilterCompanyNewFinTags,
  UniversalFilterCompanyNewFinTagsOnChange
} from './components/UniversalFilterCompanyNewFinTags';
import {
  UniversalFilterCompanyFinCategories,
  UniversalFilterCompanyFinCategoriesOnChange
} from './components/UniversalFilterCompanyFinCategories';

interface UniversalFilterProps<FiltersType extends Record<string, any>> {
  viewId: ID;
  filterValue?: FiltersType[keyof FiltersType];
  fieldName: keyof FiltersType;
  type: UniversalFilterTypes;
  filterView?: UniversalFilterView;
  options?:
    | UniversalFilterTextFilterOptions
    | UniversalFilterNumberFilterOptions
    | UniversalFilterTaskStatusFilterOptions
    | UniversalFilterUsersExtendedFilterOptions
    | UniversalFilterInvoiceStatusFilterOptions
    | UniversalFilterInvoiceProcessingJobStatusFilterOptions
    | UniversalFilterInvoicesFilterOptions
    | UniversalFilterItemTagsFilterOptions
    | UniversalFilterUsersFilterOptions;
  i18nName: I18nText;
  initialAutoFocus?: boolean;
  setFilters: (cb: (prevFilters: FiltersType) => FiltersType) => void;
  projectNanoId?: ProjectNanoID;
}

function UniversalFilter<FiltersType extends Record<string, any>>({
  viewId,
  filterValue,
  fieldName,
  type,
  filterView,
  options,
  i18nName,
  initialAutoFocus,
  setFilters,
  projectNanoId
}: UniversalFilterProps<FiltersType>) {
  const [autoFocus, setAutoFocus] = useState<boolean>(initialAutoFocus);

  useMountEffect(() => {
    if (!autoFocus) {
      setTimeout(() => {
        setAutoFocus(true);
      }, 100);
    }
  });

  const handleChange = useCallback<
    | UniversalFilterCheckboxOnChange
    | UniversalFilterCompaniesOnChange
    | UniversalFilterOperationStateOnChange
    | UniversalFilterOperationTypeOnChange
    | UniversalFilterDateOnChange
    | UniversalFilterProjectStatusOnChange
    | UniversalFilterCompanyStatusOnChange
    | UniversalFilterCompanyNewFinStatusOnChange
    | UniversalFilterCompanyNewFinTagsOnChange
    | UniversalFilterCompanyFinCategoriesOnChange
    | UniversalFilterTagsOnChange
    | UniversalFilterTaskStatusOnChange
    | UniversalFilterTextOnChange
    | UniversalFilterTextPlainOnChange
    | UniversalFilterUsersOnChange
    | UniversalFilterUsersExtendedOnChange
    | UniversalFilterInvoicesOnChange
    | UniversalFilterTaskItemsOnChange
  >(
    (value) => {
      setFilters((prevFilters) => {
        if (isNil(value)) {
          return omit(prevFilters, [fieldName]) as FiltersType;
        } else {
          return { ...prevFilters, [fieldName]: value };
        }
      });
    },
    [fieldName, setFilters]
  );

  const filterStateCacheKey = UniversalFilterSavedState.getCacheKey({
    viewId,
    fieldName
  });

  switch (type) {
    case UniversalFilterTypes.CHECKBOX:
      return (
        <UniversalFilterCheckbox
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          onChange={handleChange as UniversalFilterCheckboxOnChange}
        />
      );

    case UniversalFilterTypes.COMPANY_NANO_ID:
      return (
        <UniversalFilterCompanyNanoId
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterCompanyNanoIdOnChange}
        />
      );

    case UniversalFilterTypes.COMPANIES:
      return (
        <UniversalFilterCompanies
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterCompaniesOnChange}
        />
      );

    case UniversalFilterTypes.DATE:
      return (
        <UniversalFilterDate
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterDateOnChange}
        />
      );

    case UniversalFilterTypes.OPERATION_STATE:
      return (
        <UniversalFilterOperationState
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterOperationStateOnChange}
        />
      );

    case UniversalFilterTypes.OPERATION_TYPE:
      return (
        <UniversalFilterOperationType
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterOperationTypeOnChange}
        />
      );

    case UniversalFilterTypes.PROJECT_STATUS:
      return (
        <UniversalFilterProjectStatus
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTaskStatusOnChange}
        />
      );

    case UniversalFilterTypes.COMPANY_STATUS:
      return (
        <UniversalFilterCompanyStatus
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTaskStatusOnChange}
        />
      );

    case UniversalFilterTypes.COMPANY_NEW_FIN_STATUS:
      return (
        <UniversalFilterCompanyNewFinStatus
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTaskStatusOnChange}
        />
      );

    case UniversalFilterTypes.COMPANY_NEW_FIN_TAGS:
      return (
        <UniversalFilterCompanyNewFinTags
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTagIdOnChange}
        />
      );

    case UniversalFilterTypes.COMPANY_FIN_CATEGORIES:
      return (
        <UniversalFilterCompanyFinCategories
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTagIdOnChange}
        />
      );

    case UniversalFilterTypes.TAG_ID:
      return (
        <UniversalFilterTagId
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTagIdOnChange}
        />
      );

    case UniversalFilterTypes.TAGS:
      return (
        <UniversalFilterTags
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTagsOnChange}
        />
      );

    case UniversalFilterTypes.TASK_ITEMS:
      return (
        <UniversalFilterTaskItems
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTaskItemsOnChange}
          projectNanoId={projectNanoId}
        />
      );

    case UniversalFilterTypes.TASK_STATUS:
      return (
        <UniversalFilterTaskStatus
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterTaskStatusFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTaskStatusOnChange}
        />
      );

    case UniversalFilterTypes.TEXT:
      return (
        <UniversalFilterText
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterTextFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTextOnChange}
        />
      );

    case UniversalFilterTypes.NUMBER:
      return (
        <UniversalFilterNumber
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterNumberFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterNumberOnChange}
        />
      );

    case UniversalFilterTypes.TEXT_PLAIN:
      return (
        <UniversalFilterTextPlain
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterTextPlainOnChange}
        />
      );

    case UniversalFilterTypes.USERS:
      return (
        <UniversalFilterUsers
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterUsersFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterUsersOnChange}
        />
      );

    case UniversalFilterTypes.USERS_EXTENDED:
      return (
        <UniversalFilterUsersExtended
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterUsersExtendedFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterUsersExtendedOnChange}
        />
      );

    case UniversalFilterTypes.INVOICES_ID:
      return (
        <UniversalFilterInvoices
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterInvoicesFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterInvoicesOnChange}
        />
      );

    case UniversalFilterTypes.ITEM_TAGS_ID:
      return (
        <UniversalFilterItemTags
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={options as UniversalFilterItemTagsFilterOptions}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterItemTagsOnChange}
        />
      );

    case UniversalFilterTypes.BINDING_STATUS:
      return (
        <UniversalFilterItemBindingStatus
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          autoFocus={autoFocus}
          onChange={handleChange as UniversalFilterItemBindingStatusOnChange}
        />
      );

    case UniversalFilterTypes.INVOICE_PROCESSING_JOB_STATUS:
      return (
        <UniversalFilterInvoiceProcessingJobStatus
          filterView={filterView}
          filterStateCacheKey={filterStateCacheKey}
          filterValue={filterValue}
          i18nName={i18nName}
          options={
            options as UniversalFilterInvoiceProcessingJobStatusFilterOptions
          }
          autoFocus={autoFocus}
          onChange={
            handleChange as UniversalFilterInvoiceProcessingJobStatusOnChange
          }
        />
      );

    default:
      return null;
  }
}

export default UniversalFilter;
